/**
 * formatMatchValue — formats an ExtractionMatch value based on the QIT question type.
 * Shared across FileDetail, InsightsReview, and GroupSufficiencyCard.
 */
import { fCurrency, fPercent, fNumber } from 'src/utils/formatNumber';

export function formatMatchValue(
  value: string,
  questionType: string | null | undefined
): string {
  if (!value || !questionType) {
    return value;
  }
  const qt = questionType.toLowerCase();

  // Phone — format 10-digit string as (XXX) XXX-XXXX
  // Mirrors the backend normalizer: strip country code if 11 digits starting with 1.
  if (qt === 'phone') {
    let digits = value.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return value; // leave unrecognized formats untouched
  }

  // SSN — always show only last 4 digits masked
  if (qt === 'ssn') {
    const digits = value.replace(/\D/g, '');
    const last4 = digits.slice(-4);
    return `***-**-${last4}`;
  }

  // Last 4 SSN — just show the 4 digits
  if (qt === 'last_ssn') {
    const digits = value.replace(/\D/g, '');
    return `****${digits.slice(-4)}`;
  }

  // Frequency — title-case each word for display (e.g. "weekly" → "Weekly")
  if (qt === 'frequency') {
    return value
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  const num = parseFloat(value.replace(/[^0-9.-]/g, ''));
  if (isNaN(num)) {
    return value;
  }
  if (qt === 'currency') {
    return fCurrency(num);
  }
  if (qt === 'percentage') {
    return fPercent(num);
  }
  if (qt === 'number' || qt === 'calculation') {
    return fNumber(num);
  }
  return value;
}
